import { getLoyaltyPoints } from "apis/vip-biz.api";
import { Field, Image, Intro, Text } from "components/commons";
import { CashInMethod, FleetStatus, Page, Path, PLBTypes, UserAccess } from "enums";
import {
  CreditCards,
  FailurePurchase,
  History,
  OnlineBanking,
  OverTheCounter,
  OverTheCounterComingSoon,
  SuccessPurchase,
  VIPBizPoints,
} from "images";
import locale from "localization";
import { useContext, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import CashinCard from "./cashin-card/cashin-card";
import ManualBanking from "./cashin-manual/manual-bank-form";
import SelectBankForm from "./cashin-online-banking/select-bank-form";
import styles from "./cashin.module.scss";
import VipBizPointsForm from "./vip-biz-points/vip-biz-points-form";

import { CircularProgress } from "@material-ui/core";
import { getCashIn, getFuelBalance } from "apis";
import { getFleetCards, updateFleetCard } from "apis/fleet.api";
import { getWallet } from "apis/wallet.api";
import Big from "big.js";
import SelectPanels from "components/commons/select-panel/select-panels";
import { NotifModal } from "components/modals";
import { AnalyticsContext, FeatureFlagContext, FleetContext, UserContext } from "contexts";
import { useApi, useModal, useMount } from "hooks";
import { useCallback } from "react";
import {
  formatNumber,
  formatVolume,
  getRemainingLimitProps,
  handleRequest,
  normalizeNullableNumber,
  normalizeNumber,
  prettifyCashInMethod,
} from "utils";
import BpiOtpModal from "./bpi-otp-modal";
import CashInLimitModal from "./cashin-limit-modal/cashin-limit-modal";
import { HelpOutline } from "@material-ui/icons";
import CashinCardAddCardSuccessModal from "./cashin-card/cashin-card-add-card-success-modal";
import { otcs } from "./cashin-online-banking/banks";
import CashInOverTheCounter from "./cashin-over-the-counter/cashin-over-the-counter";

const CashInModule = () => {
  const { hasFeature } = useContext(FeatureFlagContext);
  const { page } = useContext(AnalyticsContext);
  const { fleet } = useContext(FleetContext);

  const { userAccess } = useContext(UserContext);

  const getUserAccess = userAccess.find((item) => {
    return item.key === UserAccess.CashIn;
  });

  const hasModifyAccess = getUserAccess?.fullAccess || getUserAccess?.modifyAccess ? false : true;

  const { fleetId, status } = fleet || {};
  const deactivated = status === FleetStatus.Deactivated;
  const history = useHistory();
  const location = useLocation();
  const { state = {} } = location || {};
  const [cashInMethod, setCashInMethod] = useState(state?.paymentMethod || CashInMethod.Card);
  const [showCashInLimitTooltip, setShowCashInLimitTooltip] = useState(false);
  const notifModal = useModal();
  const bpiOtpModal = useModal();
  const cashinCardAddCardSuccessModal = useModal();
  const {
    request: getWalletBalanceRequest,
    loading: fetchingWalletBalance,
    result: wallet = {},
  } = useApi({
    api: getWallet,
    params: {
      fleetId,
    },
    pageError: true,
  });
  const limits = {
      daily: fleet?.dailyCashinLimit,
      weekly: fleet?.weeklyCashinLimit,
      monthly: fleet?.monthlyCashinLimit,
    };
  const remainingLimitProps = useMemo(() => {
    return getRemainingLimitProps(limits);
  }, [limits]);
  const {
    request: getLoyaltyPointsRequest,
    loading: fetchingLoyaltyPoints,
    result: pointsBalance = {},
  } = useApi({
    api: getLoyaltyPoints,
    params: {
      fleetId,
    },
    modalError: false,
  });

  const { request: getFuelBalanceRequest, result: fuelBalance = [] } = useApi({
    api: getFuelBalance,
    params: {
      fleetId,
    },
    pageError: true,
  });

  const {
    request: getFleetCardsRequest,
    loading: fetchingFleetCards,
    result: getFleetCardsResult = { fleetCards: [], count: 0 },
  } = useApi({
    api: getFleetCards,
    params: {
      fleetId,
    },
    pageError: true,
  });

  const { request: getCashInRequest } = useApi({
    api: getCashIn,
  });

  const { request: updateFleetCardRequest } = useApi({
    api: updateFleetCard,
    modalError: false,
    pageError: false,
  });

  const { walletBalance = 0 } = wallet;
  const { points = 0 } = pointsBalance;
  const { fleetCards = [] } = getFleetCardsResult;

  const fetchFleetCards = useCallback(
    (rs) => {
      getFleetCardsRequest({ ...rs });
    },
    [getFleetCardsRequest],
  );

  const initCashInModal = useCallback(async () => {
    if (state?.cashInId) {
      const cashIn = await getCashInRequest({ cashInId: state.cashInId });
      const obj = {
        success: {
          content: locale.yourPaymentHasBeenConfirmed,
          title: locale.successTitle,
          secondary: {
            text: locale.buyFuel,
            onClick: () => {
              history.push(Path.BuyFuel);
            },
          },
          primaryText: locale.newCashIn,
          image: SuccessPurchase,
          actions: [
            {
              text: locale.goToCashInHistory,
              onClick: () => {
                history.push(Path.CashInHistory);
              },
            },
          ],
        },
        failed: {
          content: locale.thereWasAnErrorProcessingPayment,
          title: locale.cashInTransactionFailed,
          secondary: null,
          primaryText: locale.backToCashIn,
          image: FailurePurchase,
        },
        cancel: {
          content: locale.thisTransactionWasCanceled,
          title: locale.cashInTransactionCancel,
          secondary: null,
          primaryText: locale.backToCashIn,
          image: FailurePurchase,
        },
      };
      const modalStatus = obj[cashIn?.status] || obj.failed;
      notifModal.show({
        cashInId: state?.cashInId,
        amount: cashIn?.amount,
        paymentMethod: cashIn?.cashinChannel ? prettifyCashInMethod(cashIn?.cashinChannel) : "",
        ...modalStatus,
      });

      history.replace({ ...history.location, state: {} });
    }

    if (
      state?.cashInStatus &&
      state?.referenceNumber &&
      state?.paymentMethod === CashInMethod.Card
    ) {
      const obj = {
        success: {
          content: locale.youMayNowContinueYourCashInTransaction,
          title: locale.cardSuccessfullyAdded,
          primaryText: locale.continue,
        },
        failure: {
          content: locale.weEncounteredAnErrorWhileSavingYourCardDetails,
          title: locale.addingOfCardFailed,
          secondary: null,
          primaryText: locale.gotIt,
        },
      };

      const modalStatus = obj[state?.cashInStatus] || obj.failure;

      handleRequest(
        async () => {
          await updateFleetCardRequest({
            referenceNumber: state?.referenceNumber,
            fleetId,
          });
          fetchFleetCards();
          cashinCardAddCardSuccessModal.show({
            ...modalStatus,
          });
        },
        null,
        (err) => {
          cashinCardAddCardSuccessModal.show({
            ...modalStatus,
          });
        },
      );
      history.replace({ ...history.location, state: {} });
    }
  }, [
    notifModal,
    state,
    cashinCardAddCardSuccessModal,
    fetchFleetCards,
    fleetId,
    getCashInRequest,
    history,
    updateFleetCardRequest,
  ]);

  useMount(() => {
    if (state?.bank === "BPI") {
      bpiOtpModal.show();
    } else {
      initCashInModal();
    }

    getWalletBalanceRequest();
    getLoyaltyPointsRequest();
    getFuelBalanceRequest();
    fetchFleetCards();

    page({
      name: Page.ViewCashIn,
    });
  });

  const channels = useMemo(() => {
    var options = [
      {
        method: CashInMethod.Card,
        image: CreditCards,
        isEnabled: deactivated ? false : true,
        styles: styles.logo,
        form: !fetchingFleetCards ? (
          <CashinCard
            balance={walletBalance}
            loading={fetchingWalletBalance}
            fleetCards={fleetCards}
            disabled={deactivated || hasModifyAccess}
            {...remainingLimitProps}
            fetchFleetCardsRequest={() => {
              fetchFleetCards();
            }}
          />
        ) : (
          <CircularProgress />
        ),
      },
      {
        method: CashInMethod.OverTheCounter,
        image: hasFeature("CASHIN_DRAGONPAY") ? OverTheCounter : OverTheCounterComingSoon,
        isEnabled: hasFeature("CASHIN_DRAGONPAY"),
        styles: styles.image,
        form: hasFeature("CASHIN_DRAGONPAY") ? (
          <SelectBankForm
            balance={walletBalance}
            loading={fetchingWalletBalance}
            disabled={deactivated || hasModifyAccess}
            title={locale.selectAnOverTheCounterBank}
            bankList={otcs}
            paymentFollowUp={"2 days"}
            {...remainingLimitProps}
          />
        ) : (
          <CashInOverTheCounter />
        ),
      },
      {
        method: CashInMethod.OnlineBanking,
        image: OnlineBanking,
        isEnabled: deactivated ? false : true,
        styles: styles.logo,
        form: (
          <SelectBankForm
            balance={walletBalance}
            loading={fetchingWalletBalance}
            disabled={deactivated || hasModifyAccess}
            {...remainingLimitProps}
          />
        ),
      },
    ];

    fleet.isVip &&
      options.push({
        method: CashInMethod.VIPBizPoints,
        image: VIPBizPoints,
        isEnabled: !deactivated,
        styles: styles.logo,
        form: (
          <VipBizPointsForm
            balance={points}
            loading={fetchingLoyaltyPoints}
            disabled={deactivated || hasModifyAccess}
            refetch={getLoyaltyPointsRequest}
            setPaymentMethod={setCashInMethod}
            {...remainingLimitProps}
          />
        ),
      });

    options.push({
      method: CashInMethod.ManualBankDeposit,
      image: OnlineBanking,
      isEnabled: true,
      styles: styles.logo,
      form: <ManualBanking title={locale.cashInProcessInstruction} />,
    });

    return options;
  }, [
    fleet.isVip,
    deactivated,
    fetchingFleetCards,
    walletBalance,
    fetchingWalletBalance,
    fleetCards,
    hasModifyAccess,
    limits,
    hasFeature,
    fetchFleetCards,
    fetchingLoyaltyPoints,
    points,
    getLoyaltyPointsRequest,
  ]);

  const currentLiterBalance = useMemo(() => {
    let clb = Big(0);
    if (fuelBalance.length > 0) {
      fuelBalance.forEach((fb) => {
        clb = Big(clb).add(Big(fb.fuelBalance));
      });
    }
    return formatVolume(clb.toNumber());
  }, [fuelBalance]);

  const cashInLimitTooltipData = useMemo(() => {
    const dailyLimit = normalizeNullableNumber(limits?.daily?.max, 0);
    const dailyCurrent = normalizeNumber(limits?.daily?.remaining, 0);

    const weeklyLimit = normalizeNullableNumber(limits?.weekly?.max, 0);
    const weeklyCurrent = normalizeNumber(limits?.weekly?.remaining, 0);

    const monthlyLimit = normalizeNullableNumber(limits?.monthly?.max, 0);
    const monthlyCurrent = normalizeNumber(limits?.monthly?.remaining, 0);

    return [
      {
        period: "Daily",
        resetInfo: locale.cashInLimitDailyText || "Resets daily at 12 midnight",
        current: dailyCurrent,
        limit: dailyLimit,
      },
      {
        period: "Weekly",
        resetInfo: locale.cashInLimitWeeklyText || "Resets every Tuesday at 7am",
        current: weeklyCurrent,
        limit: weeklyLimit,
      },
      {
        period: "Monthly",
        resetInfo:
          locale.cashInLimitMonthlyText ||
          "Resets every first day of the month at 12 midnight",
        current: monthlyCurrent,
        limit: monthlyLimit,
      },
    ];
  }, [limits, fleet]);

  const lowestCashInLimit = useMemo(() => {
    const entries = [
      { label: "Daily", data: limits?.daily },
      { label: "Weekly", data: limits?.weekly },
      { label: "Monthly", data: limits?.monthly },
    ].filter((entry) => entry.data?.remaining != null);

    if (!entries.length) {
      return null;
    }

    return entries.reduce((lowest, current) => {
      if (!lowest || current.data.remaining < lowest.data.remaining) {
        return current;
      }
      return lowest;
    });
  }, [limits]);

  const form = useMemo(() => {
    const c = channels.find((c) => c.method === cashInMethod);
    return c?.form || null;
  }, [channels, cashInMethod]);

  return (
    <div>
      <NotifModal
        className={styles.modal}
        {...notifModal}
        title={notifModal.referenceNumber ? locale.cardSuccessfullyAdded : notifModal.title}
        primary={{
          text: notifModal.primaryText,
          onClick: () => {
            notifModal.close();
          },
        }}
      >
        <div className={styles.summary}>
          {notifModal.referenceNumber ? (
            <Text>{locale.youMayNowContinueYourCashInTransaction}</Text>
          ) : (
            [
              {
                title: locale.cashInId,
                value: notifModal.cashInId || 0,
              },
              {
                title: locale.paymentMethod,
                value: notifModal.paymentMethod || prettifyCashInMethod(CashInMethod.Card),
              },
              {
                title: locale.amount,
                value: `₱${formatNumber(notifModal.amount, 2)}` || `₱${formatNumber(0, 2)}`,
              },
            ].map((item, index) => {
              return (
                <Field
                  className={styles.field}
                  childrenClassName={styles.value}
                  label={item.title}
                  key={index}
                  labelPosition="left"
                >
                  <Text strong>{item.value}</Text>
                </Field>
              );
            })
          )}
        </div>
      </NotifModal>
      <CashinCardAddCardSuccessModal {...cashinCardAddCardSuccessModal} />
      <div className={styles.intro}>
        <Intro
          actionOnClick={() => {
            history.push(Path.CashInHistory);
          }}
          actionText={
            getUserAccess?.fullAccess || getUserAccess.viewAccess ? locale.cashInHistory : ""
          }
          actionOutlined
          actionStartIcon={<Image src={History} />}
          title={locale.cashIn}
        />
      </div>
      <div className={styles["inventory-stats"]}>
        {fleet.plbType === PLBTypes.PLB_HEDGING && (
          <>
            <div className={styles["stat-item"]}>
              <div className={styles["stat-label"]}>
                {locale.fuelInventoryLimitText || "Fuel Inventory Limit"}
              </div>
              <div className={styles["stat-value"]}>
                {Boolean(fleet?.inventoryLimit) ? formatVolume(fleet.inventoryLimit) : formatVolume(0)}
              </div>
            </div>

            <div className={styles["stat-item"]}>
              <div className={styles["stat-label"]}>
                Current Liter Balance
              </div>
              <div className={styles["stat-value"]}>
                {currentLiterBalance || formatVolume(0)}
              </div>
            </div>
          </>
        )}

        <div className={`${styles["stat-item"]} ${styles["cashin-limit-item"]}`}>
          <div className={styles["stat-label"]}>
            {locale.cashInLimitText || "Cash In Limit"}
            <span
              className={styles["info-wrapper"]}
              onMouseEnter={() => {
                setShowCashInLimitTooltip(true);
              }}
              onMouseLeave={() => {
                setShowCashInLimitTooltip(false);
              }}
            >
              <HelpOutline style={{ fontSize: "18px", color: "#7c3aed" }} />
              <CashInLimitModal
                isOpen={showCashInLimitTooltip}
                limits={cashInLimitTooltipData}
              />
            </span>
          </div>
          <div className={styles["stat-value"]}>
            {lowestCashInLimit === null
              ? "Unlimited"
              : `₱ ${formatNumber(lowestCashInLimit.data.remaining.toString(), 2)}`}
            <span className={styles["stat-subtitle"]}>
              {lowestCashInLimit === null
                ? locale.remainingDailyLimit || "(Remaining Daily Limit)"
                : `(Remaining ${lowestCashInLimit.label} Limit)`}
            </span>
          </div>
        </div>
      </div>
      <div className={styles.channel}>
        <SelectPanels
          disabled={hasModifyAccess}
          label={locale.selectCashInChannel}
          active={cashInMethod}
          items={channels.map((c) => ({
            onClick: (item) => {
              if (c.isEnabled) {
                setCashInMethod(item.value);
              }
            },
            title: prettifyCashInMethod(c.method),
            value: c.method,
            content: (
              <div className={styles.panel}>
                <Image src={c.image} className={c.styles} />
              </div>
            ),
            className: styles.cashInPaymentMethodPanel,
            enabled: c.isEnabled,
          }))}
        />
      </div>
      <BpiOtpModal {...bpiOtpModal} notifModal={notifModal} {...state} />
      {form}
    </div>
  );
};

export default CashInModule;
